// Shared blog payload helpers. The backend (basic Blog model) serves a lean
// payload — excerpt/cover/view/author{ name, avatarUrl }/keywords/highlights
// plus `content` on detail — so anything the UI renders beyond that (read time,
// author role/bio, a guaranteed cover) is derived or defaulted here. One
// module owns it so the list, detail, cards and widgets never drift apart.

// Packaged fallback for articles whose backend cover is missing or unusable.
// Seeded blogs have no image (`image` is nullable), so the cards must keep a
// designed fallback per the task requirement.
export const DEFAULT_BLOG_COVER = '/assets/images/covers/cover_1.jpg';

// A single حرف-style initial block the detail hero and author card use when a
// backend author ships without an avatar image.
export const DEFAULT_BLOG_AVATAR = '/assets/images/avatars/avatar_1.jpg';

// Average adult silent-reading speed. Used to derive an honest read-time
// estimate from the rendered body instead of inventing a random number.
const WORDS_PER_MINUTE = 200;

// A value counts as a usable image only when it is a non-empty string that is
// not the backend model default placeholder (`image.jpg`), which has no file
// behind it on seeded rows.
function hasUsableImage(value) {
  if (typeof value !== 'string') {
    return false;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return false;
  }

  return !/(^|\/)image\.jpg$/i.test(trimmed);
}

function resolveCover(value) {
  return hasUsableImage(value) ? value : DEFAULT_BLOG_COVER;
}

function resolveAvatar(value) {
  return hasUsableImage(value) ? value : DEFAULT_BLOG_AVATAR;
}

// Arrays arrive as `null` on rows the admin never touched (JSONField nulls),
// so every list render must guard instead of assuming an array.
function toStringArray(value) {
  return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.length > 0) : [];
}

// Strip markup (Quill HTML or markdown) down to words for the estimator.
export function countWords(text) {
  if (typeof text !== 'string' || !text.length) {
    return 0;
  }

  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/[#>*_`|~[\](){}<>/-]/g, ' ')
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
}

// Word count drives the read-time estimate so the badge always matches the
// body the reader is actually looking at.
function estimateReadTime(text) {
  return Math.max(1, Math.ceil(countWords(text) / WORDS_PER_MINUTE));
}

// Normalise one backend row into the post shape the pages and cards render:
// { id, slug, title, category, excerpt, cover, keywords, highlights, view,
//   comment, share, favorite, readTime, createdAt, updatedAt, author, content }.
export function normalizeBlogPost(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const author = raw.author && typeof raw.author === 'object' ? raw.author : {};
  const authorName = typeof author.name === 'string' && author.name.trim() ? author.name.trim() : 'ASAP UAE';
  const content = typeof raw.content === 'string' ? raw.content : '';
  const readTime =
    Number.isFinite(raw.readTime) && raw.readTime > 0 ? raw.readTime : estimateReadTime(content || raw.excerpt || '');

  return {
    id: raw.id ?? raw.slug ?? raw.title,
    slug: raw.slug,
    title: raw.title,
    category: raw.category,
    excerpt: raw.excerpt ?? '',
    cover: resolveCover(raw.cover),
    keywords: toStringArray(raw.keywords),
    highlights: toStringArray(raw.highlights),
    view: Number.isFinite(raw.view) ? raw.view : 0,
    comment: Number.isFinite(raw.comment) ? raw.comment : 0,
    share: Number.isFinite(raw.share) ? raw.share : 0,
    favorite: Number.isFinite(raw.favorite) ? raw.favorite : 0,
    readTime,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    author: {
      name: authorName,
      avatarUrl: resolveAvatar(author.avatarUrl),
      // The backend user model has no role/bio fields yet, so these arrive
      // undefined and the cards/hydrator omit those lines until it grows them.
      role: typeof author.role === 'string' ? author.role : undefined,
      bio: typeof author.bio === 'string' ? author.bio : undefined,
    },
    content,
  };
}

export function normalizeBlogPosts(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.map(normalizeBlogPost).filter(Boolean);
}
